import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Progress {
  id: number;
  juego_id: number;
  tipo_nivel: string;
  nivel_id: number;
  tablero_id: number | null;
  mejor_tiempo: number;
  menos_movimientos: number;
  fecha_completado: string;
  juego_nombre?: string;
  dificultad_nombre?: string;
}

interface UserStats {
  total_partidas: number;
  tiempo_total: number;
  juegos_completados: number;
  racha_actual: number;
  promedio_tiempo: number;
  juego_favorito?: string;
}

export default function ProgressPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [recentProgress, setRecentProgress] = useState<Progress[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(20);

  useEffect(() => {
    loadData();
  }, [limit]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Cargar estadísticas
      const statsResponse = await api.get('/stats');
      setStats(statsResponse.data.data);

      // Cargar progreso reciente
      const progressResponse = await api.get('/progress');
      setRecentProgress(progressResponse.data.data.slice(0, limit));
    } catch (error) {
      console.error('Error cargando datos de progreso:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    if (!seconds) return '-';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-4xl font-bold text-white">📊 Mi Progreso</h1>
            <button
              onClick={() => navigate('/juegos')}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-semibold transition-all"
            >
              🎮 Volver a Juegos
            </button>
          </div>

          {/* Estadísticas generales */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Niveles completados</div>
                <div className="text-3xl font-bold text-white">{stats.total_partidas}</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Tiempo total</div>
                <div className="text-3xl font-bold text-white">{formatTime(stats.tiempo_total)}</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Racha actual</div>
                <div className="text-3xl font-bold text-white">{stats.racha_actual} 🔥</div>
              </div>
              <div className="bg-white/20 rounded-xl p-4 text-center">
                <div className="text-sm text-white/70 mb-1">Promedio</div>
                <div className="text-3xl font-bold text-white">
                  {stats.promedio_tiempo ? formatTime(stats.promedio_tiempo) : '-'}
                </div>
              </div>
            </div>
          )}

          {stats?.juego_favorito && (
            <div className="mt-4 text-center">
              <span className="text-white/70">Juego favorito: </span>
              <span className="text-white font-bold">{stats.juego_favorito}</span>
            </div>
          )}
        </div>

        {/* Últimas sesiones */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">🕐 Últimas sesiones</h2>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
              className="px-4 py-2 bg-white/20 text-white rounded-lg font-semibold border-2 border-white/30 focus:outline-none focus:border-white/50"
            >
              <option value={20}>20 sesiones</option>
              <option value={50}>50 sesiones</option>
              <option value={100}>100 sesiones</option>
            </select>
          </div>

          {recentProgress.length === 0 ? (
            <div className="text-white/70 text-center py-8">
              Sin sesiones registradas todavía. ¡Empieza a jugar!
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 text-sm text-white/70">Juego</th>
                    <th className="text-left py-3 px-4 text-sm text-white/70">Nivel</th>
                    <th className="text-left py-3 px-4 text-sm text-white/70">Tiempo</th>
                    <th className="text-left py-3 px-4 text-sm text-white/70">Movimientos</th>
                    <th className="text-left py-3 px-4 text-sm text-white/70">Fecha</th>
                    <th className="text-left py-3 px-4 text-sm text-white/70"></th>
                  </tr>
                </thead>
                <tbody>
                  {recentProgress.map((progress) => (
                    <tr
                      key={progress.id}
                      className="border-b border-white/10 hover:bg-white/5 transition-colors"
                    >
                      <td className="py-3 px-4 text-white">
                        {progress.juego_nombre || progress.tipo_nivel}
                      </td>
                      <td className="py-3 px-4 text-white/90">
                        {progress.dificultad_nombre || `Nivel ${progress.nivel_id}`}
                        {progress.tablero_id && (
                          <span className="text-white/60 ml-1">#{progress.tablero_id}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-white/90">
                        {formatTime(progress.mejor_tiempo)}
                      </td>
                      <td className="py-3 px-4 text-white/90">
                        {progress.menos_movimientos || '-'}
                      </td>
                      <td className="py-3 px-4 text-white/70 text-sm">
                        {formatDate(progress.fecha_completado)}
                      </td>
                      <td className="py-3 px-4">
                        {progress.tablero_id && (
                          <button
                            onClick={() => {
                              const gameCode = progress.tipo_nivel;
                              const nivelId = progress.nivel_id;
                              const tableroId = progress.tablero_id;

                              // Construir URL según el juego
                              let url = `/juegos/${gameCode}?dificultad=${nivelId}`;

                              // Algunos juegos usan 'tableroId', otros 'seed'
                              if (['bombillas', 'binary', 'hitori', 'kakurasu'].includes(gameCode)) {
                                url += `&seed=${tableroId}`;
                              } else {
                                url += `&tableroId=${tableroId}`;
                              }

                              navigate(url);
                            }}
                            className="px-3 py-1 bg-blue-500/80 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition-all"
                          >
                            Ir →
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Botón para ir a rutina */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/rutina')}
            className="px-8 py-3 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-white rounded-xl font-bold text-lg shadow-lg transition-all transform hover:scale-105"
          >
            📅 Ver Mi Rutina Diaria
          </button>
        </div>
      </div>
    </div>
  );
}
